import React from "react";
import styled from "styled-components";
import Button from "./Button";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import Menu from "./Menu";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "../ErrorFallback";
import { TOrganization } from "../../../redux/reducers/organizations";
import { localStorageConstants } from "../../../utils/constants";

const Container = styled.div`
  position: relative;
  width: 280px;
`;

export type TVariants = "labeled" | "unlabeled";

type TLabeledMenu = {
  variant: "labeled";
  labels: string[];
};

type TUnlabeledMenu = {
  variant: "unlabeled";
};

export type TOptGroupOptions = "Últimos acessados" | "Todos";

export type TInfiniteScrollingVariants = "organizations" | "nonPaginated";

export type TInfiniteScroller = {
  variant: TInfiniteScrollingVariants;
  totalElements: number;
  searchParam?: string;
  onIntersect?: (data: any[], elementsAmount: number) => void;
};

type TCommonPropsMenu = {
  shouldHideActiveIcon?: boolean;
  search: boolean;
  optGroup?: TOptGroupOptions[];
  onSelect?: (selected: string) => void;
  customFilter?: (
    index: string,
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    selectDataRef: React.MutableRefObject<string[]>,
    setShouldUpdate: React.Dispatch<React.SetStateAction<boolean>>
  ) => void;
  options: string[];
  forceSelectOptionsByIndex?: number[];
  searchPhraseOnChange?: (searchPhrase: string) => void;
  searchPhraseOnSearch?: (searchPhrase: string) => void;
  hasInfiniteScrolling?: TInfiniteScroller;
  setSelectedIndex?: (
    setSelectedData: React.Dispatch<React.SetStateAction<string[]>>
  ) => void;
  disabledOptions?: Array<string | number>;
};

type TCommonButton = {
  placeholder: string;
  onError?: { hasError: boolean; message: string };
  filled?: string;
  disabled?: boolean;
  hasMandatoryStyle?: boolean;
};

type TButtonBase = TCommonButton & {
  variant: TVariants;
  labels: string[];
  showPlaceholder?: boolean;
};

type TButtonSingle = TButtonBase & {
  selectType?: "single";
  setData?: (
    setSelectedData: React.Dispatch<React.SetStateAction<string>>
  ) => void;
};

type TButtonMulti = TButtonBase & {
  selectType: "multi";
  initialMultiSelectLabels?: string[];
  setData?: (
    setSelectedData: React.Dispatch<React.SetStateAction<string[]>>
  ) => void;
  onRemove?: (removedTag: string, updatedSelected: string[]) => void;
};

type TButton = TButtonSingle | TButtonMulti;

type TMenu<V extends TVariants> = V extends "labeled"
  ? TLabeledMenu & TCommonPropsMenu
  : TUnlabeledMenu & TCommonPropsMenu;

type TDropdownProps<V extends TVariants> = {
  button: TButton;
  menu: TMenu<V>;
  selectedOrganization?: TOrganization | null;
  onItemLoadingChange?: (
    loading: Record<string, boolean>,
    setLoading: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  ) => void;
};

const Dropdown: React.FC<TDropdownProps<TVariants>> = ({
  button,
  menu,
  selectedOrganization,
  onItemLoadingChange,
}) => {
  const [selectedData, setSelectedData] = useState<string>("");
  const [selectedDataMulti, setSelectedDataMulti] = useState<string[]>(
    button.selectType === "multi" ? button.initialMultiSelectLabels ?? [] : []
  );

  const [shouldShow, setShouldShow] = useState<"show" | "hide">("hide");
  const [itemLoading, setItemLoading] = useState<Record<string, boolean>>({});

  const lastLoadingKeyRef = useRef<string | null>(null);

  const componentRef = useRef<HTMLDivElement | null>(null);
  const buttonSetIsClickedRef = useRef<React.Dispatch<
    React.SetStateAction<boolean>
  > | null>(null);

  const searchId = menu.search ? useId() : "";
  const [shouldUpdate, setShouldUpdate] = useState(false);

  const selectDataRef = useRef([""]);

  const handleButtonRender = useCallback(() => {
    const handleOnClickButton = (
      setIsClicked: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      buttonSetIsClickedRef.current = setIsClicked;
      setShouldShow((prev) => (prev === "hide" ? "show" : "hide"));
    };

    const handleOnBlur = (
      e: React.FocusEvent<HTMLButtonElement, Element>
    ): "hide" | "show" => {
      if (menu.search) {
        const searchElement = document.getElementById(searchId);
        if (
          searchElement &&
          e.relatedTarget &&
          Array.from(searchElement?.children).includes(e.relatedTarget)
        ) {
          return "show";
        } else {
          setTimeout(() => {
            setShouldShow("hide");
          }, 20);
          return "hide";
        }
      } else {
        setTimeout(() => {
          setShouldShow("hide");
        }, 20);
        return "hide";
      }
    };

    switch (button.variant) {
      case "labeled":
        const handleNoPlaceholderLabel = () => {
          if (button.selectType === "multi") {
            if (selectedDataMulti.length > 0) {
              return selectedDataMulti
                .map((val) => val.replace(/(.*)?%.*/, "$1"))
                .join(", ");
            }
            if (selectedOrganization && selectedOrganization.company_name) {
              return selectedOrganization.company_name;
            }
            return (button.labels && button.labels[0]) ?? "";
          } else {
            const selectedLabel = selectedData?.replace(/(.*)?%.*/, "$1");
            if (selectedLabel?.length > 0) {
              return selectedLabel;
            }
            if (selectedOrganization && selectedOrganization.company_name) {
              return selectedOrganization.company_name;
            }
            return (button.labels && button.labels[0]) ?? selectedLabel;
          }
        };

        if (button.selectType === "multi") {
          if (button.setData && selectedDataMulti.length === 0)
            button.setData(setSelectedDataMulti);
        } else {
          if (button.setData && selectedData.length === 0)
            button.setData(setSelectedData);
        }

        return (
          <Button
            key={selectedData}
            multiSelect={
              button.selectType === "multi"
                ? {
                    labels: selectedDataMulti,
                    onRemove(tag) {
                      setTimeout(() => {
                        setSelectedDataMulti((prev) => {
                          const result = prev.filter((item) => item !== tag);

                          if (button.onRemove) {
                            button.onRemove(tag, result);
                          }

                          return result;
                        });
                      }, 500);
                    },
                  }
                : undefined
            }
            disabled={button.disabled}
            onBlur={handleOnBlur}
            onClick={handleOnClickButton}
            placeholder={button.placeholder}
            variant="labeled"
            onError={button.onError}
            label={
              button.showPlaceholder
                ? button.filled ?? selectedData?.replace(/(.*)?%.*/, "$1")
                : handleNoPlaceholderLabel()
            }
            componentRef={componentRef}
            hasMandatoryStyle={button.hasMandatoryStyle}
          />
        );
      case "unlabeled":
        const handleUnlabeledOnBlur = () => {
          setTimeout(() => {
            setShouldShow("hide");
          }, 20);
          return "hide" as const;
        };

        if (button.selectType === "multi") {
          if (button.setData && selectedDataMulti.length === 0)
            button.setData(setSelectedDataMulti);
        } else {
          if (button.setData && selectedData.length === 0)
            button.setData(setSelectedData);
        }

        return (
          <Button
            disabled={button.disabled}
            multiSelect={
              button.selectType === "multi"
                ? {
                    labels: selectedDataMulti,
                    onRemove(tag) {
                      setTimeout(() => {
                        setSelectedDataMulti((prev) => {
                          const result = prev.filter((item) => item !== tag);

                          if (button.onRemove) {
                            button.onRemove(tag, result);
                          }

                          return result;
                        });
                      }, 500);
                    },
                  }
                : undefined
            }
            onBlur={handleUnlabeledOnBlur}
            onClick={handleOnClickButton}
            placeholder={button.placeholder}
            variant="unlabeled"
            label={selectedData?.length > 0 ? selectedData : button.labels[0]}
            componentRef={componentRef}
            hasMandatoryStyle={button.hasMandatoryStyle}
          />
        );
    }
  }, [selectedData, selectedDataMulti, button]);

  const handleMenuRender = useCallback(() => {
    const handleOnClickMenu = (
      index: string,
      selected: string[],
      setSelected: React.Dispatch<React.SetStateAction<string[]>>,
      stringValue?: string
    ) => {
      let value = stringValue;
      if (!value) {
        if (index.startsWith("lastAccessed-")) {
          const lastAccessedIdx = parseInt(index.replace("lastAccessed-", ""));
          let orgs: any[] = [];
          try {
            const raw = localStorage.getItem(
              localStorageConstants.LAST_ACCESSED_ORGANIZATIONS
            );
            if (raw) orgs = JSON.parse(raw);
          } catch (e) {
            orgs = [];
          }
          value = orgs[lastAccessedIdx]?.cnpj as string;
        } else {
          value = menu.options[parseInt(index)];
        }
      }

      if (menu.customFilter) {
        menu.customFilter(
          index,
          selected,
          setSelected,
          selectDataRef,
          setShouldUpdate
        );
        if (button.selectType !== "multi") {
          setTimeout(() => {
            if (buttonSetIsClickedRef.current)
              buttonSetIsClickedRef.current(false);
            setShouldShow("hide");
          }, 20);
        }
      } else {
        if (button.variant === "labeled") {
          if (button.selectType === "multi") {
            // Stack selected data for multiselection
            setSelectedDataMulti((prev) => {
              if (prev.includes(value ?? "")) return prev;
              return [...prev, value ?? ""];
            });
            setSelected((prev) => {
              if (prev.includes(value ?? "")) return prev;
              return [...prev, value ?? ""];
            });
          } else {
            if (menu.variant === "labeled") {
              setSelectedData(value ?? "");
              setSelected([value ?? ""]);
            } else {
              setSelectedData(value ?? "");
              setSelected([value ?? ""]);
            }
          }
        } else {
          if (button.selectType === "multi") {
            // Stack selected data for multiselection
            setSelectedDataMulti((prev) => {
              if (prev.includes(value ?? "")) return prev;
              return [...prev, value ?? ""];
            });
            setSelected((prev) => {
              if (prev.includes(value ?? "")) return prev;
              return [...prev, value ?? ""];
            });
          } else {
            setSelectedData(value ?? "");
            setSelected([value ?? ""]);
          }
        }

        if (menu.onSelect) {
          menu.onSelect(value ?? "");

          if (onItemLoadingChange) {
            // Always track the last selected key for async options
            lastLoadingKeyRef.current = value!;
          }
        }

        // For local/hardcoded options, close menu immediately
        if (!onItemLoadingChange && button.selectType !== "multi") {
          setTimeout(() => {
            if (buttonSetIsClickedRef.current)
              buttonSetIsClickedRef.current(false);
            setShouldShow("hide");
          }, 20);
        }
      }
    };
    switch (menu.variant) {
      case "labeled":
        return (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Menu
              shouldHideActiveIcon={menu.shouldHideActiveIcon}
              searchId={searchId.length > 0 ? searchId : ""}
              shouldShow={shouldShow}
              variant="labeled"
              label={menu.labels}
              content={menu.options}
              list="default"
              onClick={handleOnClickMenu}
              optGroup={menu.optGroup}
              forceSelectOptionsByIndex={menu.forceSelectOptionsByIndex}
              searchPhraseOnChange={menu.searchPhraseOnChange}
              searchPhraseOnSearch={menu.searchPhraseOnSearch}
              hasInfiniteScrolling={menu.hasInfiniteScrolling}
              setData={(setSelectedData) => {
                if (menu.setSelectedIndex)
                  menu.setSelectedIndex(setSelectedData);
              }}
              disabledOptions={menu.disabledOptions}
              itemLoading={itemLoading}
              setItemLoading={setItemLoading}
            />
          </ErrorBoundary>
        );
      case "unlabeled":
        return (
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Menu
              shouldHideActiveIcon={menu.shouldHideActiveIcon}
              searchId={searchId.length > 0 ? searchId : ""}
              shouldShow={shouldShow}
              variant="unlabeled"
              content={menu.options}
              list="default"
              onClick={handleOnClickMenu}
              optGroup={menu.optGroup}
              forceSelectOptionsByIndex={menu.forceSelectOptionsByIndex}
              searchPhraseOnChange={menu.searchPhraseOnChange}
              searchPhraseOnSearch={menu.searchPhraseOnSearch}
              setData={(setSelectedData) => {
                if (menu.setSelectedIndex)
                  menu.setSelectedIndex(setSelectedData);
              }}
              disabledOptions={menu.disabledOptions}
              itemLoading={itemLoading}
              setItemLoading={setItemLoading}
            />
          </ErrorBoundary>
        );
    }
  }, [shouldShow, menu, shouldUpdate, itemLoading]);

  const handleRenderDropdown = useCallback(() => {
    return (
      <Container ref={componentRef}>
        {handleButtonRender()}
        {!button.disabled && handleMenuRender()}
      </Container>
    );
  }, [handleButtonRender, handleMenuRender]);

  useEffect(() => {
    if (onItemLoadingChange) {
      onItemLoadingChange(itemLoading, setItemLoading);
    }
  }, [itemLoading, onItemLoadingChange]);

  useEffect(() => {
    // Only close the menu if the last loading key is set and its loading is now false or undefined
    if (lastLoadingKeyRef.current && !itemLoading[lastLoadingKeyRef.current]) {
      setTimeout(() => {
        console.log("closing menu after async load");
        if (buttonSetIsClickedRef.current) buttonSetIsClickedRef.current(false);
        setShouldShow("hide");
        lastLoadingKeyRef.current = null;
      }, 20);
    }
  }, [itemLoading]);

  return handleRenderDropdown();
};

export default React.memo(Dropdown);
